import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  moodZones,
  physicalStateOptions,
  belongingOptions,
  cannedMessages,
  notificationChannelOptions,
  getTodayStr,
  getNowTimestamp,
  type PreInspectionLog,
  type NotificationChannel,
} from '@/mocks/preInspection';

interface PreInspectionModalProps {
  open: boolean;
  onClose: () => void;
  studentId: string;
  studentName: string;
  className: string;
  onSave: (log: PreInspectionLog) => void;
  existingLogs: PreInspectionLog[];
  userRole: string;
  userName: string;
}

export default function PreInspectionModal({
  open,
  onClose,
  studentId,
  studentName,
  className,
  onSave,
  existingLogs,
  userRole,
  userName,
}: PreInspectionModalProps) {
  const { t } = useTranslation();
  const [selectedMoodId, setSelectedMoodId] = useState<string>('');
  const [selectedPhysicalStates, setSelectedPhysicalStates] = useState<string[]>([]);
  const [selectedBelongings, setSelectedBelongings] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const [notifyParent, setNotifyParent] = useState(false);
  const [notificationChannels, setNotificationChannels] = useState<NotificationChannel[]>([]);
  const [showCannedMessages, setShowCannedMessages] = useState(false);
  const [saved, setSaved] = useState(false);
  const [viewLogsTab, setViewLogsTab] = useState<'form' | 'history'>('form');

  const selectedZone = useMemo(() => {
    if (!selectedMoodId) return null;
    for (const zone of moodZones) {
      if (zone.moods.some((m) => m.id === selectedMoodId)) return zone;
    }
    return null;
  }, [selectedMoodId]);

  const studentLogs = useMemo(() => {
    return existingLogs.filter((l) => l.studentId === studentId).sort((a, b) => b.timestamp.localeCompare(a.timestamp));
  }, [existingLogs, studentId]);

  const todayLogExists = useMemo(() => {
    const today = getTodayStr();
    return studentLogs.some((l) => l.date === today);
  }, [studentLogs]);

  const togglePhysicalState = (id: string) => {
    setSelectedPhysicalStates((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id],
    );
  };

  const toggleBelonging = (id: string) => {
    setSelectedBelongings((prev) =>
      prev.includes(id) ? prev.filter((bel) => bel !== id) : [...prev, id],
    );
  };

  const toggleNotificationChannel = (ch: NotificationChannel) => {
    setNotificationChannels((prev) =>
      prev.includes(ch) ? prev.filter((c) => c !== ch) : [...prev, ch],
    );
  };

  const applyCannedMessage = (text: string) => {
    setNotes((prev) => (prev ? `${prev}\n${text}` : text));
    setShowCannedMessages(false);
  };

  const handleSave = () => {
    if (!selectedMoodId) return;

    const zone = moodZones.find((z) => z.moods.some((m) => m.id === selectedMoodId));
    const log: PreInspectionLog = {
      id: `pi-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      studentId,
      studentName,
      className,
      date: getTodayStr(),
      timestamp: getNowTimestamp(),
      moodId: selectedMoodId,
      moodZone: zone?.id || 'green',
      physicalStates: selectedPhysicalStates,
      belongings: selectedBelongings,
      notes,
      notifyParent,
      notificationChannels: notifyParent ? notificationChannels : [],
      notificationSent: notifyParent && notificationChannels.length > 0,
      createdBy: userName,
    };

    onSave(log);
    setSaved(true);
  };

  const handleClose = () => {
    setSelectedMoodId('');
    setSelectedPhysicalStates([]);
    setSelectedBelongings([]);
    setNotes('');
    setNotifyParent(false);
    setNotificationChannels([]);
    setShowCannedMessages(false);
    setSaved(false);
    setViewLogsTab('form');
    onClose();
  };

  if (!open) return null;

  const zoneColors: Record<string, string> = {
    blue: 'bg-blue-500 border-blue-300',
    green: 'bg-green-500 border-green-300',
    yellow: 'bg-yellow-400 border-yellow-300',
    red: 'bg-red-500 border-red-300',
  };

  const zoneBgColors: Record<string, string> = {
    blue: 'bg-blue-50',
    green: 'bg-green-50',
    yellow: 'bg-yellow-50',
    red: 'bg-red-50',
  };

  const zoneBorderColors: Record<string, string> = {
    blue: 'border-blue-300',
    green: 'border-green-300',
    yellow: 'border-yellow-300',
    red: 'border-red-300',
  };

  const renderHistoryTab = () => (
    <div className="flex flex-col gap-3">
      {studentLogs.length === 0 ? (
        <div className="py-10 text-center">
          <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-3">
            <i className="ri-clipboard-line text-foreground-300 text-xl" />
          </div>
          <p className="text-sm font-label text-foreground-500">{t('attendance.noPreInspectionLogs')}</p>
          <p className="text-xs text-foreground-400 mt-1">{t('attendance.noPreInspectionLogsHint')}</p>
        </div>
      ) : (
        studentLogs.map((log) => {
          const mood = moodZones.flatMap((z) => z.moods).find((m) => m.id === log.moodId);
          const zone = moodZones.find((z) => z.id === log.moodZone);
          return (
            <div
              key={log.id}
              className={`p-3 rounded-xl border ${zoneBorderColors[log.moodZone] || 'border-background-200'} ${zoneBgColors[log.moodZone] || 'bg-background-50'}`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-lg">{mood?.emoji || '🙂'}</span>
                  <div>
                    <p className="text-sm font-label font-medium text-foreground-800">{mood?.label || 'Not recorded'}</p>
                    <p className="text-[10px] text-foreground-400 font-label">
                      {new Date(log.timestamp).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                      {' · '}
                      {new Date(log.timestamp).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
                <span className={`px-2 py-0.5 text-[10px] font-label font-medium rounded-full ${zoneBgColors[log.moodZone]} ${zoneBorderColors[log.moodZone]}`}>
                  {zone?.label || ''}
                </span>
              </div>

              {log.physicalStates.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2">
                  {log.physicalStates.map((ps) => {
                    const opt = physicalStateOptions.find((o) => o.id === ps);
                    return (
                      <span key={ps} className="px-2 py-0.5 text-[10px] font-label rounded-full bg-background-100 text-foreground-600 border border-background-200">
                        {opt?.label || ps}
                      </span>
                    );
                  })}
                </div>
              )}

              {log.belongings.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-2">
                  {log.belongings.map((b) => {
                    const opt = belongingOptions.find((o) => o.id === b);
                    return (
                      <span key={b} className="px-2 py-0.5 text-[10px] font-label rounded-full bg-accent-50 text-accent-700 border border-accent-100">
                        <i className={`${opt?.icon || 'ri-check-line'} text-[10px] mr-1`} />
                        {opt?.label || b}
                      </span>
                    );
                  })}
                </div>
              )}

              {log.notes && (
                <p className="text-xs text-foreground-600 font-label mb-2 leading-relaxed">{log.notes}</p>
              )}

              <div className="flex items-center gap-3 text-[10px] text-foreground-400 font-label">
                <span>{t('attendance.recordedBy')}: {log.createdBy}</span>
                {log.notifyParent && log.notificationSent && (
                  <span className="flex items-center gap-1 text-green-600">
                    <i className="ri-check-double-line text-xs" />
                    {t('attendance.parentNotified')}
                  </span>
                )}
              </div>
            </div>
          );
        })
      )}
    </div>
  );

  const renderFormTab = () => (
    <div className="flex flex-col gap-5">
      {/* Mood Selection */}
      <div>
        <label className="block text-sm font-label font-semibold text-foreground-800 mb-3">
          {t('attendance.studentMood')} <span className="text-red-500">*</span>
        </label>
        <div className="grid grid-cols-1 gap-2">
          {moodZones.map((zone) => (
            <div
              key={zone.id}
              className={`p-3 rounded-xl border-2 transition-all ${
                selectedZone?.id === zone.id
                  ? `${zoneBorderColors[zone.id]} ${zoneBgColors[zone.id]} shadow-sm`
                  : 'border-transparent bg-background-50'
              }`}
            >
              <p className="text-xs font-label font-semibold text-foreground-600 mb-2 flex items-center gap-1.5">
                <span className={`w-2.5 h-2.5 rounded-full ${zoneColors[zone.id]}`} />
                {zone.label}
              </p>
              <div className="grid grid-cols-2 gap-1.5">
                {zone.moods.map((mood) => (
                  <button
                    key={mood.id}
                    onClick={() => setSelectedMoodId(mood.id)}
                    className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-left transition-all cursor-pointer whitespace-nowrap ${
                      selectedMoodId === mood.id
                        ? `${zoneColors[mood.zone]} text-white font-medium shadow-sm`
                        : `bg-white hover:bg-background-100 border border-background-200 text-foreground-700`
                    }`}
                  >
                    <span className="text-lg flex-shrink-0">{mood.emoji}</span>
                    <span className="text-xs font-label leading-tight">{mood.label}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Physical State */}
      <div>
        <label className="block text-sm font-label font-semibold text-foreground-800 mb-2">
          {t('attendance.physicalState')}
        </label>
        <div className="flex flex-wrap gap-2">
          {physicalStateOptions.map((opt) => (
            <button
              key={opt.id}
              onClick={() => togglePhysicalState(opt.id)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-label border transition-all cursor-pointer whitespace-nowrap ${
                selectedPhysicalStates.includes(opt.id)
                  ? 'bg-secondary-100 border-secondary-300 text-secondary-800 font-medium'
                  : 'bg-background-50 border-background-200 text-foreground-600 hover:bg-background-100'
              }`}
            >
              <i className={`${opt.icon} text-sm`} />
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Belongings */}
      <div>
        <label className="block text-sm font-label font-semibold text-foreground-800 mb-2">
          {t('attendance.belongingsOnPickup')}
        </label>
        <div className="flex flex-wrap gap-2">
          {belongingOptions.map((opt) => (
            <button
              key={opt.id}
              onClick={() => toggleBelonging(opt.id)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-label border transition-all cursor-pointer whitespace-nowrap ${
                selectedBelongings.includes(opt.id)
                  ? 'bg-accent-100 border-accent-300 text-accent-800 font-medium'
                  : 'bg-background-50 border-background-200 text-foreground-600 hover:bg-background-100'
              }`}
            >
              <i className={`${opt.icon} text-sm`} />
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Notes */}
      <div>
        <label className="block text-sm font-label font-semibold text-foreground-800 mb-2">
          {t('attendance.additionalNotes')}
        </label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder={t('attendance.notesPlaceholder')}
          maxLength={500}
          rows={3}
          className="w-full px-3 py-2.5 text-sm font-label rounded-lg border border-background-200 bg-background-50 text-foreground-700 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-secondary-300 focus:border-secondary-400 resize-none"
        />
        <div className="flex items-center justify-between mt-1.5">
          <button
            onClick={() => setShowCannedMessages(!showCannedMessages)}
            className="flex items-center gap-1 text-xs font-label text-secondary-600 hover:text-secondary-800 cursor-pointer whitespace-nowrap"
          >
            <i className={`${showCannedMessages ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-sm`} />
            {t('attendance.cannedMessages')}
          </button>
          <span className="text-[10px] text-foreground-400 font-label">{notes.length}/500</span>
        </div>

        {showCannedMessages && (
          <div className="mt-2 flex flex-col gap-1 max-h-40 overflow-y-auto">
            {cannedMessages.map((msg) => (
              <button
                key={msg.id}
                onClick={() => applyCannedMessage(msg.text)}
                className="text-left px-2.5 py-2 text-xs font-label text-foreground-600 hover:bg-secondary-50 hover:text-secondary-800 rounded-lg border border-background-100 hover:border-secondary-200 transition-colors cursor-pointer"
              >
                {msg.text}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Parent Notification Toggle */}
      <div className="border-t border-background-200 pt-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-sm font-label font-semibold text-foreground-800">{t('attendance.notifyParent')}</p>
            <p className="text-[11px] text-foreground-400 font-label">{t('attendance.notifyParentHint')}</p>
          </div>
          <button
            onClick={() => setNotifyParent(!notifyParent)}
            className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors duration-200 cursor-pointer whitespace-nowrap ${
              notifyParent ? 'bg-semantic-success' : 'bg-background-300'
            }`}
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform duration-200 shadow-sm ${
                notifyParent ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {notifyParent && (
          <div className="p-3 rounded-xl bg-background-50 border border-background-200">
            <p className="text-xs font-label font-medium text-foreground-600 mb-2">{t('attendance.sendVia')}</p>
            <div className="flex flex-wrap gap-2 mb-3">
              {notificationChannelOptions.map((ch) => (
                <button
                  key={ch.id}
                  onClick={() => toggleNotificationChannel(ch.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-label border transition-all cursor-pointer whitespace-nowrap ${
                    notificationChannels.includes(ch.id)
                      ? 'bg-accent-100 border-accent-300 text-accent-800 font-medium'
                      : 'bg-white border-background-200 text-foreground-600 hover:bg-background-100'
                  }`}
                >
                  <i className={`${ch.icon} text-sm`} />
                  {ch.label}
                </button>
              ))}
            </div>

            <div className="p-3 rounded-lg bg-secondary-50 border border-secondary-100">
              <p className="text-[10px] font-label font-semibold text-foreground-500 uppercase tracking-wide mb-1">{t('attendance.messagePreview')}</p>
              <p className="text-xs font-label text-foreground-700 leading-relaxed whitespace-pre-line">
                {(() => {
                  const mood = moodZones.flatMap((z) => z.moods).find((m) => m.id === selectedMoodId);
                  const zone = moodZones.find((z) => z.id === selectedZone?.id);
                  const moodText = mood ? `${mood.emoji} ${mood.label}` : t('attendance.notRecorded');
                  const zoneText = zone ? `(${zone.label})` : '';
                  return `📋 *${t('attendance.preInspectionUpdate')} — ${studentName}*\n\n${t('attendance.dateLabel')}: ${getTodayStr()}\n🧠 ${t('attendance.moodLabel')}: ${moodText} ${zoneText}${notes ? `\n📝 ${t('attendance.notesLabel')}: ${notes}` : ''}\n\n— ${userName}`;
                })()}
              </p>
            </div>
          </div>
        )}
      </div>

      {todayLogExists && viewLogsTab === 'form' && (
        <div className="p-3 rounded-xl bg-yellow-50 border border-yellow-200 flex items-start gap-3">
          <i className="ri-information-line text-yellow-600 text-sm mt-0.5 flex-shrink-0" />
          <p className="text-xs font-label text-yellow-700">{t('attendance.todayLogExists')}</p>
        </div>
      )}

      {/* Save button */}
      <div className="flex items-center gap-3 pt-2 border-t border-background-200">
        <button
          onClick={handleClose}
          className="px-4 py-2.5 text-sm font-label font-medium rounded-lg border border-background-200 bg-background-50 text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
        >
          {t('common.cancel')}
        </button>
        <button
          onClick={handleSave}
          disabled={!selectedMoodId}
          className="flex-1 px-4 py-2.5 text-sm font-label font-medium rounded-lg bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {t('attendance.savePreInspection')}
        </button>
      </div>
    </div>
  );

  const renderSavedState = () => (
    <div className="py-10 text-center">
      <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-green-50 mb-4">
        <i className="ri-check-double-line text-green-500 text-3xl" />
      </div>
      <p className="text-base font-label font-semibold text-foreground-800 mb-1">{t('attendance.preInspectionSaved')}</p>
      <p className="text-sm text-foreground-500 font-label mb-5">{t('attendance.preInspectionSavedHint', { name: studentName })}</p>
      <div className="flex items-center justify-center gap-3">
        <button
          onClick={() => {
            setSaved(false);
            setSelectedMoodId('');
            setSelectedPhysicalStates([]);
            setSelectedBelongings([]);
            setNotes('');
            setNotifyParent(false);
            setNotificationChannels([]);
          }}
          className="px-4 py-2 text-sm font-label font-medium rounded-lg bg-accent-100 text-accent-800 border border-accent-200 hover:bg-accent-200 transition-colors cursor-pointer whitespace-nowrap"
        >
          {t('attendance.logAnother')}
        </button>
        <button
          onClick={handleClose}
          className="px-4 py-2 text-sm font-label font-medium rounded-lg bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          {t('common.close')}
        </button>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={handleClose} />
      <div className="relative bg-background-50 rounded-2xl shadow-floating max-w-2xl w-full max-h-[90vh] flex flex-col animate-[modalEnter_200ms_cubic-bezier(0.4,0,0.2,1)]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-background-200 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-blue-50">
              <i className="ri-clipboard-line text-blue-600 text-lg" />
            </div>
            <div>
              <h2 className="text-base font-heading font-700 text-foreground-900">{t('attendance.preInspectionTransitLogging')}</h2>
              <p className="text-xs text-foreground-400 font-label">
                {studentName} — {className}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {/* Tab switcher */}
            <div className="flex items-center bg-background-100 rounded-full p-1 mr-2">
              <button
                onClick={() => setViewLogsTab('form')}
                className={`px-3 py-1.5 text-xs font-label font-medium rounded-full transition-all cursor-pointer whitespace-nowrap ${
                  viewLogsTab === 'form'
                    ? 'bg-white text-foreground-800 shadow-sm'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                {t('attendance.newLog')}
              </button>
              <button
                onClick={() => setViewLogsTab('history')}
                className={`px-3 py-1.5 text-xs font-label font-medium rounded-full transition-all cursor-pointer whitespace-nowrap ${
                  viewLogsTab === 'history'
                    ? 'bg-white text-foreground-800 shadow-sm'
                    : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                {t('attendance.logHistory')}
                {studentLogs.length > 0 && (
                  <span className="ml-1.5 px-1.5 py-0.5 text-[10px] rounded-full bg-accent-100 text-accent-700">{studentLogs.length}</span>
                )}
              </button>
            </div>
            <button
              onClick={handleClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:text-foreground-600 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-close-line text-lg" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {saved ? renderSavedState() : viewLogsTab === 'history' ? renderHistoryTab() : renderFormTab()}
        </div>
      </div>
    </div>
  );
}